import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { buildGroupStats, groupMeta, humanizeGroup } from './constants';

// ── One nav row ───────────────────────────────────────────────────────────────

const NavItem = ({ active, dot, label, total, unset, onClick, nested }) => (
  <button
    type="button"
    onClick={onClick}
    aria-current={active ? 'true' : undefined}
    className={[
      'tw-group tw-flex tw-items-center tw-gap-2 tw-w-full tw-rounded-lg tw-border-0 tw-cursor-pointer tw-transition-colors tw-text-left',
      nested ? 'tw-px-2 tw-py-1.5' : 'tw-px-2.5 tw-py-2',
      active ? 'tw-bg-primary-50 tw-text-primary-800' : 'tw-bg-transparent tw-text-slate-600 hover:tw-bg-slate-100',
    ].join(' ')}
  >
    {dot !== null && (
      <span
        className="tw-w-1.5 tw-h-1.5 tw-rounded-full tw-shrink-0"
        style={{ background: dot || (active ? 'currentColor' : '#cbd5e1') }}
      />
    )}
    <span className={`tw-flex-1 tw-truncate ${nested ? 'tw-text-[12px]' : 'tw-text-[13px]'} ${active ? 'tw-font-semibold' : 'tw-font-medium'}`}>
      {label}
    </span>

    {/* The number that matters while setting the portal up is how many keys in
        here still have no value — so that is the one rendered in colour. */}
    {unset > 0 ? (
      <span className="tw-text-[10px] tw-font-bold tw-tabular-nums tw-px-1.5 tw-py-0.5 tw-rounded tw-bg-amber-100 tw-text-amber-700 tw-shrink-0">
        {unset}
      </span>
    ) : (
      <span className="tw-text-[11px] tw-tabular-nums tw-text-slate-400 tw-shrink-0">{total}</span>
    )}
  </button>
);

NavItem.propTypes = {
  active: PropTypes.bool,
  dot: PropTypes.string,
  label: PropTypes.string.isRequired,
  total: PropTypes.number.isRequired,
  unset: PropTypes.number.isRequired,
  onClick: PropTypes.func.isRequired,
  nested: PropTypes.bool,
};

// ── Sidebar ───────────────────────────────────────────────────────────────────

const SettingsSidebar = ({
  data, groups, subGroupLabels, activeGroup, activeSubGroup, onSelectGroup, onSelectSubGroup,
}) => {
  const stats = useMemo(() => buildGroupStats(data), [data]);

  const groupKeys = [...new Set([...Object.keys(groups), ...Object.keys(stats.byGroup)])]
    .filter((key) => stats.byGroup[key]?.total);

  const activeSubs = Object.entries(stats.byGroup[activeGroup]?.subs || {});
  // Server order first (it is meaningful), then anything it does not know about.
  const orderedSubs = [
    ...Object.keys(subGroupLabels).filter((key) => stats.byGroup[activeGroup]?.subs[key]),
    ...activeSubs.map(([key]) => key).filter((key) => !subGroupLabels[key]),
  ];

  return (
    <nav className="tw-w-56 tw-shrink-0 tw-flex tw-flex-col tw-gap-1">
      <p className="tw-text-[10px] tw-font-bold tw-text-slate-400 tw-uppercase tw-tracking-widest tw-px-2.5 tw-mb-1">
        Groups
      </p>

      <NavItem
        active={activeGroup === 'all'}
        dot={null}
        label="All settings"
        total={stats.total}
        unset={stats.unset}
        onClick={() => onSelectGroup('all')}
      />

      {groupKeys.map((key) => {
        const bucket = stats.byGroup[key];
        const active = activeGroup === key;

        return (
          <React.Fragment key={key}>
            <NavItem
              active={active && !activeSubGroup}
              dot={groupMeta(key).dot}
              label={groups[key] || humanizeGroup(key)}
              total={bucket.total}
              unset={bucket.unset}
              onClick={() => onSelectGroup(key)}
            />

            {active && orderedSubs.length > 0 && (
              <div className="tw-flex tw-flex-col tw-gap-0.5 tw-ml-3.5 tw-pl-2 tw-border-l tw-border-slate-200 tw-mb-1">
                {orderedSubs.map((subKey) => (
                  <NavItem
                    key={subKey}
                    nested
                    active={activeSubGroup === subKey}
                    dot={null}
                    label={subGroupLabels[subKey] || humanizeGroup(subKey)}
                    total={bucket.subs[subKey].total}
                    unset={bucket.subs[subKey].unset}
                    onClick={() => onSelectSubGroup(activeSubGroup === subKey ? null : subKey)}
                  />
                ))}
              </div>
            )}
          </React.Fragment>
        );
      })}
    </nav>
  );
};

SettingsSidebar.propTypes = {
  data: PropTypes.array.isRequired,
  groups: PropTypes.objectOf(PropTypes.string).isRequired,
  subGroupLabels: PropTypes.objectOf(PropTypes.string).isRequired,
  activeGroup: PropTypes.string.isRequired,
  activeSubGroup: PropTypes.string,
  onSelectGroup: PropTypes.func.isRequired,
  onSelectSubGroup: PropTypes.func.isRequired,
};

export default SettingsSidebar;
